<template>
  <CustomModal class="goal-preview-modal" @close="$emit('close')" style="top: -100px">
    <template v-slot:body>
      <div class="goal-preview-modal__body">
        <GoalPreviewInfo v-bind="currentGoal"/>
        <GoalPreviewStats v-bind="currentGoal"/>
        <div class="col-span-2 relative" style="height: 275px;">
          <GoalChart v-if="chartDataLoaded" :height="250"
                     :goal_currency="currentGoal.currency"
                     :chart-data="chartData"/>
          <div v-else class="flex justify-center items-center h-full text-text">
            <font-awesome-icon icon="circle-notch" spin style="font-size: 3rem;"/>
          </div>
        </div>
        <template v-if="!currentUserIsWatcher">
          <template v-if="currentGoal.status === 2">
            <div class="col-span-2 flex items-center justify-center">
              <BaseButton class="lg:w-78"
                          theme="red"
                          :loading="submitted"
                          @click="archive">{{ $t('archiveGoal') }}
              </BaseButton>
            </div>
          </template>
          <template v-else>
            <div class="col-span-2 flex items-center">
              <GoalReplenishBalance :form-opened="replenishFormOpen"
                                    :goal="currentGoal"
                                    @toggleForm="replenishFormOpen = !replenishFormOpen"
                                    @successReplenishment="updateGoal($event)"/>
              <template v-if="!replenishFormOpen && !replenishFormAnimating">
                <BaseButton theme="white-bordered"
                            class="lg:w-78 ml-8"
                            :loading="submitted"
                            @click="$emit('edit-goal')">
                  {{ $t('editGoal') }}
                </BaseButton>
                <ArchiveGoal class="ml-auto"
                             :goal-account-title="currentGoal.account_title"
                             :goal-currency="currentGoal.currency"
                             :goal-balance="currentGoal.balance"
                             :submitted="submitted"
                             @confirm="archive($event)"/>
                <FinishGoal class="ml-20"
                            :goal-account-title="currentGoal.account_title"
                            :goal-currency="currentGoal.currency"
                            :goal-balance="currentGoal.balance"
                            :submitted="submitted"
                            @confirm="complete($event)"/>
              </template>
            </div>
          </template>
        </template>
      </div>
    </template>
  </CustomModal>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import CustomModal from "@/components/UI/modals/CustomModal";
import GoalPreviewInfo from "@/components/cabinet/Money/Goals/GoalPreview/GoalPreviewInfo";
import GoalPreviewStats from "@/components/cabinet/Money/Goals/GoalPreview/GoalPreviewStats/GoalPreviewStats";
import BaseButton from "@/components/UI/BaseButton";
import GoalChart from "@/components/cabinet/Money/Goals/GoalPreview/GoalChart";
import FinishGoal from "@/components/cabinet/Money/Goals/GoalPreview/FinishGoal";
import GoalReplenishBalance from "@/components/cabinet/Money/Goals/GoalPreview/GoalReplenishBalance";
import ArchiveGoal from "@/components/cabinet/Money/Goals/GoalPreview/ArchiveGoal";

export default {
  name: "GoalPreviewModal",
  components: {
    ArchiveGoal,
    FinishGoal,
    GoalReplenishBalance,
    GoalChart,
    GoalPreviewStats,
    GoalPreviewInfo,
    BaseButton,
    CustomModal
  },
  props: {
    goal: {
      type: Object
    }
  },
  data() {
    return {
      replenishFormOpen: false,
      replenishFormAnimating: false,
      submitted: false,
      chartDataLoaded: false,
      goalChartData: {
        datasets: [],
        labels: []
      },
      currentGoal: null
    }
  },
  computed: {
    ...mapGetters('space', ['currentUserIsWatcher']),
    chartData() {
      let chartBarColor = (value, date) => {
        let isFutureMonth = this.$moment().diff(this.$moment(date, 'MM.YYYY')) <= 0;
        return isFutureMonth
            ? value === 0
                ? '#edf2f5' : '#C7D6DC'
            : value === 0
                ? '#FD85A2' : '#C7D6DC';
      }

      let chartData = {
        labels: this.goalChartData.labels,
        datasets: []
      }
      let planDataset = {
        label: this.goalChartData.datasets[0].label,
        data: this.goalChartData.datasets[0].data,
        backgroundColor: '#edf2f5',
      }
      let factDataset = {
        label: this.goalChartData.datasets[1].label,
        data: this.goalChartData.datasets[1].data,
        backgroundColor: this.goalChartData.datasets[1].data.map((data, i) => chartBarColor(data, this.goalChartData.labels[i]))
      }

      chartData.datasets.push(factDataset)
      chartData.datasets.push(planDataset)
      return chartData;
    },
  },
  methods: {
    ...mapActions('space/goals', ['fetchUserGoals', 'getGoalChartData', 'completeGoal', 'deleteGoal']),
    async fetchGoalData() {
      try {
        this.chartDataLoaded = false;
        const res = await this.getGoalChartData(this.goal.id);
        if (res.body.success) {
          this.goalChartData.datasets = res.body.data.datasets;
          this.goalChartData.labels = res.body.data.labels;
          this.chartDataLoaded = true
        }
      } catch (e) {
        console.error(e)
      }
    },
    async archive(data) {
      try {
        this.submitted = true;
        let deleteData = {
          id: this.goal.id,
          ...data
        }
        const res = await this.deleteGoal(deleteData);
        if (res.body.success) {
          await this.fetchUserGoals();
          if (this.currentGoal.balance > 0) {
            this.$toasted.info(this.$t('goalArchivedAndTransferredMessage'))
          } else {
            this.$toasted.info(this.$t('goalArchivedMessage'))
          }
          this.$emit('close');
        } else {
          console.log(res)
          this.$toasted.error(this.$t('errors.response'))
        }
      } catch (err) {
        console.error(err)
      } finally {
        this.submitted = false;
      }
    },
    async complete(data) {
      try {
        this.submitted = true;
        let completeData = {
          id: this.goal.id,
          ...data
        }
        const res = await this.completeGoal(completeData);
        if (res.body.success) {
          await this.fetchUserGoals();
          if (this.currentGoal.balance > 0) {
            this.$toasted.info(this.$t('goalCompleteAndTransferredMessage'))
          } else {
            this.$toasted.info(this.$t('goalCompleteMessage'))
          }
          this.$emit('close');
        } else {
          console.log(res)
          this.$toasted.error(this.$t('errors.response'))
        }
      } catch (err) {
        console.error(err)
      } finally {
        this.submitted = false;
      }
    },
    updateGoal(goal) {
      this.currentGoal = goal;
      this.fetchGoalData();
      this.fetchUserGoals();
    }
  },
  async created() {
    this.currentGoal = this.goal;
    await this.fetchGoalData();
  },
  watch: {
    replenishFormOpen: {
      handler() {
        this.replenishFormAnimating = true;
        setTimeout(() => {
          this.replenishFormAnimating = false;
        }, 300)
      }
    }
  },
  i18n: {
    messages: {
      en: {
        editGoal: 'Edit goal',
        archiveGoal: 'Delete goal',
        goalCompleteMessage: 'Goal completed',
        goalArchivedMessage: 'Goal removed',
        goalCompleteAndTransferredMessage: 'Transfer completed, target completed',
        goalArchivedAndTransferredMessage: 'Transfer completed, target removed'
      },
      ru: {
        editGoal: 'Редактировать цель',
        archiveGoal: 'Удалить цель',
        goalCompleteMessage: 'Цель завершена',
        goalArchivedMessage: 'Цель удалена',
        goalCompleteAndTransferredMessage: 'Перевод совершен, цель завершена',
        goalArchivedAndTransferredMessage: 'Перевод совершен, цель удалена'
      },
    }
  }
}
</script>

<style lang="scss">
.goal-preview-modal {

  &__body {
    @apply grid col-gap-14 row-gap-12;
    width: 910px;
    grid-template-columns: 350px 525px;
  }

  .modal-close-icon {
    top: 44px;
  }

  @media(max-width: 1199px) {
    &__body {
      width: 720px;
    }
  }
}

</style>